import { Component, Inject, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import {
    CMPS_DIALOG_DATA,
    CmpsConfirmDialogComponent,
    CmpsConfirmDialogOptions,
    CmpsDialog,
    CmpsDialogRef,
} from '@cmps/ng-uikit';
import { EmojiEvent } from '@ctrl/ngx-emoji-mart/ngx-emoji';
import { TranslateService } from '@ngx-translate/core';
import { filter, map, tap } from 'rxjs/operators';

import { AuthService } from '../../../auth/auth.service';
import { ChatContext } from '../../../coaching-base/chat-context.model';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../../common/take-until-destroy.decorator';
import { useDispatcher, UseStore } from '../../../store.decorator';
import { ChatMessage, HubMethods, SocketService } from '../../../teams-call';
import { ChatMessageNotification } from '../../../teams-call/core/model/chat-message-notification.model';
import { ChatNotification } from '../../../teams-call/core/model/chat-notification.model';
import { copyTextToClipboard } from '../../transcript-dialog/function/copy-text-to-clipboard.function';

import { ChatBubble } from './model/chat-bubble.model';
import { ChatDialogData } from './model/chat-dialog-data.model';
import { deleteMessages } from './state/delete-messages.action';
import { load } from './state/load.action';
import { receiveMessages } from './state/receive-messages.action';
import { reload } from './state/reload.action';
import { sendMessage } from './state/send-message.action';
import { updateChat } from './state/update-chat.action';
import { TranslationKeyMap } from './teamschat-dialog.i18n';
import { teamschatDialogReducer } from './teamschat-dialog.reducer';
import { TeamschatDialogState } from './teamschat-dialog.state';
import { ChatTranscriptDialogData } from './teamschat-transcript-dialog/model/chat-transcript-dialog-data.model';
import { TeamschatTranscriptsDialogBoxComponent } from './teamschat-transcript-dialog/teamschat-transcript-dialog.component';

@Component({
    selector: 'cmps-teamschat-dialog',
    templateUrl: './teamschat-dialog.component.html',
    host: {
        class: 'cmps-teamschat-dialog',
    },
})
@TakeUntilDestroy()
@UseStore('coachingDetail.TeamschatDialog', teamschatDialogReducer)
export class TeamschatDialogComponent implements OnInit {
    state: TeamschatDialogState;

    /** @private */
    _inputMessage: string;
    /** @private */
    _i18n = TranslationKeyMap;

    /** @private */
    _isEmojiPickerVisible: boolean;
    /** @private */
    _emojiPickerHasBeenOpen: boolean;

    constructor(
        private readonly _translateService: TranslateService,
        private readonly _dialogRef: CmpsDialogRef<
            TeamschatDialogComponent,
            null
        >,
        private readonly _dialog: CmpsDialog,
        private readonly _socketService: SocketService,
        // Data should be chat participants emails and threadId if existing
        @Inject(CMPS_DIALOG_DATA) /** @private */
        readonly _data: ChatDialogData,
        private readonly _authService: AuthService,
    ) {
        this._inputMessage = '';
        this._emojiPickerHasBeenOpen = false;
    }

    ngOnInit(): void {
        this._bindChatNotification();

        this._socketService.message
            .pipe(
                filter(
                    (msg) =>
                        msg.hubMethod === HubMethods.OnChatMessage ||
                        msg.hubMethod === HubMethods.OnChatMessageList,
                ),
                map((msg) => msg.payload),
                tap((payload: ChatMessageNotification) => {
                    useDispatcher(
                        this,
                        receiveMessages({ messages: payload.messages }),
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */
    _onHasValidTokenChange(hasValidToken: boolean): void {
        if (!hasValidToken) {
            console.log('no valid msft token');
            return;
        }

        useDispatcher(
            this,
            load({
                opts: this._data.chatRequest,
            }),
        );
    }

    /** @private */
    _onSubmit(form: NgForm): void {
        const value = form.value.message as string;
        if (value == null || value.trim().length === 0) {
            return;
        }

        const message: ChatMessage = {
            message: value,
        };

        useDispatcher(this, sendMessage({ message }));

        this._inputMessage = '';
    }

    /** @private */
    _onAddNotesClick(): void {
        let ctx: ChatContext | null;
        try {
            ctx = JSON.parse(this._data.chatRequest.context);
        } catch {}

        const data: ChatTranscriptDialogData = {
            chat: this.state.chat,
            context: ctx,
            messages: this.state.conversation,
            willNavigate: () => {
                this._dialogRef.close();
            },
        };

        this._dialog.open(TeamschatTranscriptsDialogBoxComponent, {
            resizable: true,
            disableBackdrop: true,
            disableClose: true,
            movable: true,
            data,
        });
        this._dialogRef.close();
    }

    /** @private */
    _onTopicChange(value: string): void {
        if (this.state?.chat?.topic === value) {
            return;
        }
        const chat = {
            ...this.state.chat,
            topic: value,
        };
        useDispatcher(
            this,
            updateChat({
                chat,
            }),
        );
    }

    /** @private */
    _onDeleteClick(deletedBubble: ChatBubble): void {
        useDispatcher(this, deleteMessages({ messages: [deletedBubble] }));
    }

    /** @private */
    _onCopyClick(message: string): void {
        copyTextToClipboard(message);
    }

    /** @private */
    _onEmojiSelect(event: EmojiEvent): void {
        this._inputMessage = `${this._inputMessage}${
            (event.emoji.native as string) ?? ''
        }`;
        this._isEmojiPickerVisible = false;
        this._emojiPickerHasBeenOpen = false;
    }

    /** @private */
    _onShiftEnterClick(): void {
        this._inputMessage = `${this._inputMessage}\n`;
    }

    /** @private */
    _onInputChange(e): void {
        e.target.style.height = '0px';
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        e.target.style.height = e.target.scrollHeight + 25 + 'px';
    }

    /** @private */
    _onOutsideClick(): void {
        if (this._emojiPickerHasBeenOpen) {
            this._isEmojiPickerVisible = !this._isEmojiPickerVisible;
        }
        this._emojiPickerHasBeenOpen = !this._emojiPickerHasBeenOpen;
    }

    private _bindChatNotification(): void {
        const showDialog = (): void => {
            const data: CmpsConfirmDialogOptions = {
                title: this._translateService.instant(
                    TranslationKeyMap._11041_CHAT,
                ),
                message: this._translateService.instant(
                    TranslationKeyMap._11197_THERE_IS_ALREADY_A_CHAT_CREATED_FOR_THIS_COMMITMENT_,
                ),
                actions: [
                    {
                        label: this._translateService.instant(
                            TranslationKeyMap._637_OKAY,
                        ),
                        data: 0,
                    },
                ],
            };
            const ref = this._dialog.open(CmpsConfirmDialogComponent, {
                disableBackdrop: true,
                data,
            });
            ref.result
                .pipe(
                    tap(() => {
                        this._dialogRef.close();
                    }),
                    takeUntilDestroy(this),
                )
                .subscribe();
        };

        this._socketService.message
            .pipe(
                filter((msg) => msg.hubMethod === HubMethods.OnChat),
                map((msg) => msg.payload),
                tap((payload: ChatNotification) => {
                    /* this check is done here since by this point the backend would have synced the chat members */
                    const isMember = payload.chat?.members.some(
                        (e) => e.userId === this._authService.user.id,
                        // (e) => e.userId === 10,
                    );
                    if (isMember === false) {
                        showDialog();
                    } else {
                        useDispatcher(
                            this,
                            reload({
                                chat: payload.chat,
                                errorCode: payload.error?.code,
                                userId: this._authService.user.id,
                            }),
                        );
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }
}
